// PB-003 — Gate UNICO per l'annuncio della fine recupero.
//
// Problema risolto: l'avviso di fine ("beep"/vibrazione/notifica client) veniva
// riprodotto ogni volta che il client "ri-notava" la scadenza, da più percorsi
// non coordinati (tick dell'intervallo, visibilitychange/pageshow, init/mount).
// La vecchia guardia viveva in un ref per-istanza, azzerato a ogni remount.
//
// Qui l'annuncio è idempotente PER timerId e DUREVOLE:
// - marker in localStorage (sopravvive a resume, remount e cold start);
// - Set in memoria (guardia sincrona veloce nel corso della stessa sessione JS);
// entrambi impostati SINCRONI prima del check server → chiudono la gara tra
// tick/visibility/mount concorrenti sullo stesso timerId.

const ANNOUNCED_KEY = 'coachfit_rest_announced';
const announcedMem = new Set();

/** true se la fine di questo timerId è già stata annunciata (mem o durevole). */
export const isRestFinishAnnounced = (timerId) => {
  if (!timerId) return false;
  if (announcedMem.has(timerId)) return true;
  try {
    return localStorage.getItem(ANNOUNCED_KEY) === timerId;
  } catch (e) {
    return false;
  }
};

/** Marca (sincrono) la fine di questo timerId come già annunciata. */
export const markRestFinishAnnounced = (timerId) => {
  if (!timerId) return;
  announcedMem.add(timerId);
  try {
    localStorage.setItem(ANNOUNCED_KEY, timerId);
  } catch (e) { /* storage bloccato: resta la guardia in memoria */ }
};

/**
 * Annuncia la fine del recupero per `timerId` ESATTAMENTE UNA VOLTA.
 * Percorso unico condiviso da tick / visibility / mount.
 *
 * deps (iniettabili per i test):
 *  - getRestPushState(): Promise<{ enabled, row, error }>
 *  - serverAlreadyCoveredRest({ enabled, row, error, savedTimerId }): boolean
 *  - playAlert(): void        (beep/vibrazione/notifica client)
 *  - cancelPush(): void       (annulla la push server pendente)
 *
 * Ritorna { skipped } se già annunciato, altrimenti { announced, played }.
 * - marca SINCRONO prima del check async → anti-race;
 * - se il server ha già coperto quel timerId → marca ma NON riproduce;
 * - altrimenti (push off / non inviata / fallita / dubbio) → riproduce UNA volta
 *   e annulla la push pendente.
 */
export const announceRestFinish = async (timerId, deps) => {
  if (isRestFinishAnnounced(timerId)) return { skipped: true, played: false };
  markRestFinishAnnounced(timerId); // guardia sincrona anti-race (mem + durevole)

  let covered = false;
  try {
    const state = await deps.getRestPushState();
    covered = deps.serverAlreadyCoveredRest({ ...state, savedTimerId: timerId });
  } catch (e) {
    covered = false; // in dubbio: avvisa (nessuna regressione)
  }

  if (covered) {
    return { announced: true, played: false }; // push server ha già avvisato
  }
  deps.playAlert();
  deps.cancelPush();
  return { announced: true, played: true };
};
